import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { format, isSameDay } from 'date-fns'
import { ClockIcon, Tooltip } from 'examkrackers-components'
import PATHS from 'utils/paths'
import { formatMinutesToHoursAndMinutes, noTimezoneDate } from 'utils/date'
import McatDaySalty from 'assets/images/Calendar_Salty_MCATDay.svg'
import { CalendarContext } from 'hooks/CalendarContext'
import styled, { css } from 'styled-components'

import {
  isHolidayInDate,
  getHolidayName
} from 'modules/calendar/utils/calendarCell'
import {
  getSrqQuizzesDueTodayFromEvents,
  formatSrqQuizzesDueTodayCount,
  isSrqPlaceholderEvent
} from 'modules/calendar/utils/getSrqQuizzesDueTodayFromEvents'
import { useSrqStatus } from 'modules/quizDiagnostics/utils/srqStatusStore'
import { useTranslation } from 'react-i18next'

interface CalendarCellHeaderProps {
  day: string
  isToday: boolean
  isStartDate: boolean
  isMcatDate: boolean
  isEmptyMcatDay: boolean
  isFreeTrial: boolean
  startDate: Date | null
  cellFullDay: string
  isWithin: boolean
}

const CalendarCellHeader = ({
  day,
  isToday,
  isStartDate,
  isMcatDate,
  isEmptyMcatDay,
  isFreeTrial,
  startDate,
  cellFullDay,
  isWithin
}: CalendarCellHeaderProps): JSX.Element => {
  const { t } = useTranslation()
  const history = useHistory()
  const { lists, firstVisibleSunday } = useContext(CalendarContext)

  const parsedDay = noTimezoneDate(day)
  const parsedCellFullDay = noTimezoneDate(cellFullDay)
  const parsedFirstVisibleSunday = noTimezoneDate(firstVisibleSunday)

  const dayNumber = parsedDay ? parsedDay.getDate() : 0
  const shortMonthLabel = parsedDay ? format(parsedDay, 'MMM') : ''

  const isFirstVisibleSunday =
    !!parsedFirstVisibleSunday &&
    !!parsedCellFullDay &&
    isSameDay(parsedFirstVisibleSunday, parsedCellFullDay)

  const isHolidayToday = isHolidayInDate(cellFullDay)
  const holidayName = getHolidayName(cellFullDay)

  const dayEvents = lists[cellFullDay]
  const { srqRedDotsVisible } = useSrqStatus()
  const srqQuizzesDueToday = getSrqQuizzesDueTodayFromEvents(dayEvents)
  const showSrqBadge =
    srqRedDotsVisible && isToday && srqQuizzesDueToday > 0

  const totalTime =
    dayEvents?.reduce((acc, event) => {
      if (isSrqPlaceholderEvent(event)) {
        return acc
      }

      return acc + (event.duration || 0)
    }, 0) || 0

  const isBeforeStartDate =
    !!parsedCellFullDay && !!startDate && parsedCellFullDay < startDate

  const isNotIncludedInCourse = !isWithin || !isMcatDate

  const isDateDisabled: boolean = isNotIncludedInCourse || isBeforeStartDate

  const dayNumberTooltipContent = (() => {
    if (!isToday) {
      return ''
    }
    if (isStartDate) {
      return 'Start Date'
    }
    if (isMcatDate) {
      return 'MCAT Date'
    }
    return "Today's Date"
  })()

  return (
    <CellHeader data-testid='CalendarRow-CellHeader'>
      <DayNumberGroup>
        {isHolidayToday ? (
          <Tooltip
            data-testid={`CalendarRow-Tooltip-${holidayName}`}
            tooltipContent={holidayName}
            id={cellFullDay}
          >
            <DayNumber
              data-testid='CalendarRow-DayNumber'
              isHoliday={isHolidayToday}
              isStartDate={isStartDate}
              isMcatDate={isMcatDate}
              isToday={isToday}
              isDisabled={isDateDisabled}
            >
              {dayNumber === 1
                ? `${shortMonthLabel} ${dayNumber}`
                : dayNumber}
            </DayNumber>
          </Tooltip>
        ) : dayNumberTooltipContent ? (
          <Tooltip
            tooltipContent={dayNumberTooltipContent}
            id={`todays-date-${cellFullDay}`}
          >
            <DayNumber
              data-testid={`CalendarRow-DayNumber-${dayNumber}`}
              isToday={isToday}
              isStartDate={isStartDate}
              isMcatDate={isMcatDate}
              isHoliday={false}
              isDisabled={isDateDisabled}
            >
              {isStartDate || isFirstVisibleSunday || dayNumber === 1
                ? `${shortMonthLabel} ${dayNumber}`
                : dayNumber}
              {!isEmptyMcatDay && isMcatDate && (
                <MacatLabel data-testid='CalendarRow-McatLabel'>
                  MCAT
                </MacatLabel>
              )}
            </DayNumber>
          </Tooltip>
        ) : (
          <DayNumber
            data-testid={`CalendarRow-DayNumber-${dayNumber}`}
            isToday={isToday}
            isStartDate={isStartDate}
            isMcatDate={isMcatDate}
            isHoliday={false}
            isDisabled={isDateDisabled}
          >
            {isStartDate || isFirstVisibleSunday || dayNumber === 1
              ? `${shortMonthLabel} ${dayNumber}`
              : dayNumber}
            {!isEmptyMcatDay && isMcatDate && (
              <MacatLabel data-testid='CalendarRow-McatLabel'>MCAT</MacatLabel>
            )}
          </DayNumber>
        )}
        {showSrqBadge ? (
          <Tooltip
            tooltipContent={t('dashboard.calendarWidget.srqQuizzesDueTooltip')}
            id={`srq-quizzes-due-${cellFullDay}`}
          >
            <SrqQuizzesDueBadge
              aria-label={t('dashboard.calendarWidget.srqQuizzesDueTooltip')}
              onClick={() => history.push(PATHS.chapterAverages)}
            >
              {formatSrqQuizzesDueTodayCount(srqQuizzesDueToday)}
            </SrqQuizzesDueBadge>
          </Tooltip>
        ) : null}
      </DayNumberGroup>
      {totalTime > 0 && (isWithin || isFreeTrial) ? (
        <TotalTime data-testid='CalendarRow-TotalTime' totalTime={totalTime}>
          <StyledClockIcon />
          {formatMinutesToHoursAndMinutes(totalTime)}
        </TotalTime>
      ) : isMcatDate ? (
        <PartySaltyImage
          data-testid='CalendarRow-PartSaltyImage'
          id='party-salty'
          src={McatDaySalty}
          alt='Salty celebration'
        />
      ) : (
        <div />
      )}
    </CellHeader>
  )
}

export default CalendarCellHeader

const CellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const DayNumberGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`

const SrqQuizzesDueBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 15px;
  padding: 0 6px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.main.error500};
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  margin-top: 4px;
  cursor: pointer;
`

const DayNumber = styled.div<{
  isToday: boolean
  isHoliday: boolean
  isStartDate: boolean
  isMcatDate: boolean
  isDisabled: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: relative;
  z-index: 13;
  font-weight: bold;
  margin: 4px 0 0 4px;

  ${({ isToday, isHoliday, isStartDate, isMcatDate, isDisabled }) => {
    if (isToday && isHoliday && isDisabled) {
      return css`
        color: ${({ theme }) => theme.colors.main.text};
        min-width: 15px;
        padding: 0 4px;
        min-height: 15px;
        max-height: 15px;
        border-radius: 10px;
      `
    } else if (isToday && isHoliday) {
      return css`
        color: #fff;
        min-width: 22px;
        max-width: 22px;
        min-height: 22px;
        max-height: 22px;
        border-radius: 100%;
      `
    } else if (isStartDate) {
      return css`
        background-color: #119b4c;
        color: #fff;
        min-width: 15px;
        padding: 0 4px;
        min-height: 15px;
        max-height: 15px;
        border-radius: 10px;
      `
    } else if (isToday) {
      return css`
        background-color: ${({ theme }) => theme.colors.main.black};
        color: #fff;
        min-width: 15px;
        padding: 0 4px;
        min-height: 15px;
        max-height: 15px;
        border-radius: 10px;
      `
    } else if (isToday) {
      return css`
        background-color: ${({ theme }) => theme.colors.main.black};
        color: #fff;
        min-width: 15px;
        padding: 0 4px;
        min-height: 15px;
        max-height: 15px;
        border-radius: 10px;
      `
    } else if (isHoliday) {
      return css`
        cursor: help;
        color: red;
      `
    } else if (isMcatDate) {
      return css`
        background-color: ${({ theme }) =>
          theme?.colors?.calendar.exam.orange.tag};
        color: #fff;
        min-width: 15px;
        padding: 0 4px;
        min-height: 15px;
        max-height: 15px;
        border-radius: 10px;
      `
    }
  }}
`

const TotalTime = styled.div<{
  isMcatDate?: boolean
  totalTime: number
}>`
  display: ${({ isMcatDate }) => (isMcatDate ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  font-size: ${({ totalTime }) => (totalTime > 540 ? '10px' : '11px')};
  color: ${({ theme }) => theme.colors.main.grey900};
  padding-right: 8px;
  gap: 2px;
`

const StyledClockIcon = styled(ClockIcon)`
  font-size: 16px !important;
`

const PartySaltyImage = styled.img`
  position: absolute;
  top: -10px;
  right: 0;
  width: 120px;
  z-index: 11;
`

const MacatLabel = styled.div`
  margin-left: 5px;
`
